import {AnchoredOverlay, Button} from '@primer/react'
import {useMemo, useState} from 'react'

import {TriageAgentStatusIcon} from './TriageAgentStatusIcon'
import styles from './TriageAgentButton.module.css'
import {TriageAgentOverlayContent} from './TriageAgentOverlayContent'
import {useTriageAgentStatus} from './use-triage-agent-status'
import {TRIAGE_BUTTON_TEXT, type TriageState} from './utils/constants'

type TriageAgentButtonProps = {
  repoOwner: string
  repoName: string
}

const INDICATOR_CONFIG: Record<TriageState, {testId: string; ariaLabel: string; color: string}> = {
  inactive: {
    testId: 'triage-agent-status-inactive',
    ariaLabel: 'Triage agent is inactive',
    color: 'var(--borderColor-default)',
  },
  active: {
    testId: 'triage-agent-status-active',
    ariaLabel: 'Triage agent is active',
    color: 'var(--fgColor-success)',
  },
  paused: {
    testId: 'triage-agent-status-paused',
    ariaLabel: 'Triage agent is paused',
    color: 'var(--fgColor-muted)',
  },
}

const ERROR_CONFIG = {
  testId: 'triage-agent-status-error',
  ariaLabel: 'Error loading triage agent status',
  color: 'var(--fgColor-attention)',
}

function getTriageState(configExists: boolean | undefined, enabled: boolean | undefined): TriageState {
  if (!configExists) return 'inactive'
  if (enabled) return 'active'
  return 'paused'
}

function TriageAgentButtonLoading() {
  return (
    <Button disabled data-testid="triage-agent-button-loading">
      Triage agent
    </Button>
  )
}

function TriageAgentButtonLoaded({
  repoOwner,
  repoName,
  triageState,
  isError,
}: {
  repoOwner: string
  repoName: string
  triageState: TriageState
  isError: boolean
}) {
  const [isOpen, setIsOpen] = useState(false)
  const config = isError ? ERROR_CONFIG : INDICATOR_CONFIG[triageState]

  const leadingVisual = useMemo(
    () => (
      <span
        data-testid={config.testId}
        className={`${styles.statusIndicator} ${triageState === 'active' ? styles.pulse : ''}`}
        style={{color: config.color}}
      >
        <TriageAgentStatusIcon state={triageState} ariaLabel={config.ariaLabel} isError={isError} />
      </span>
    ),
    [config, triageState, isError],
  )

  return (
    <AnchoredOverlay
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      renderAnchor={props => (
        <Button {...props} leadingVisual={leadingVisual}>
          {TRIAGE_BUTTON_TEXT[triageState]}
        </Button>
      )}
    >
      <TriageAgentOverlayContent repoOwner={repoOwner} repoName={repoName} onClose={() => setIsOpen(false)} />
    </AnchoredOverlay>
  )
}

export function TriageAgentButton({repoOwner, repoName}: TriageAgentButtonProps) {
  const {data, isLoading, isError} = useTriageAgentStatus(repoOwner, repoName)

  if (isLoading) {
    return <TriageAgentButtonLoading />
  }

  const triageState = getTriageState(data?.config_exists, data?.enabled)

  return (
    <TriageAgentButtonLoaded repoOwner={repoOwner} repoName={repoName} triageState={triageState} isError={isError} />
  )
}

try{ TriageAgentButtonLoading.displayName ||= 'TriageAgentButtonLoading' } catch {}
try{ TriageAgentButtonLoaded.displayName ||= 'TriageAgentButtonLoaded' } catch {}
try{ TriageAgentButton.displayName ||= 'TriageAgentButton' } catch {}